import React, { useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';

import { constructQueryParamName } from '../../../util/search';

import IconPlus from '../IconPlus/IconPlus';
import SelectMultipleFilter from '../SelectMultipleFilter/SelectMultipleFilter';

import css from './CombinedWheelFilter.module.css';

/**
 * CombinedWheelFilter groups the wheel diameter and rim width filters
 * (front / rear) into a single collapsible panel so buyers can pick
 * both without switching between separate filter tabs.
 *
 * The URL shape is unchanged: each sub-filter still dispatches its own
 * query param (pub_diameter, pub_rimWidthFront, pub_rimWidthRear), so
 * existing bookmarked/shared URLs keep working.
 */
const CombinedWheelFilter = props => {
  const {
    idPrefix,
    diameterConfig,
    widthFrontConfig,
    widthRearConfig,
    initialValues,
    getHandleChangedValueFn,
    intl,
    ...rest
  } = props;

  // Match FilterComponent's id scheme so the desktop sidebar and the mobile
  // modal (both mounted at once, just CSS-hidden by breakpoint) don't produce
  // duplicate DOM ids for the same sub-filter / its checkboxes.
  const prefix = idPrefix || 'SearchPage';

  // combinedWheelConfigs / urlQueryParams are forwarded by FilterComponent
  // but aren't needed by the sub-filters, so pull them out before spreading
  // the rest onto SelectMultipleFilter.
  const {
    combinedWheelConfigs,
    urlQueryParams,
    liveEdit,
    showAsPopup,
    className,
    ...subRest
  } = rest;

  const [open, setOpen] = useState(false);

  const useHistoryPush = liveEdit || showAsPopup;
  // Both sub-filters share the same change handler so they update their
  // own URL param independently while living within one panel.
  const handleChange = getHandleChangedValueFn(useHistoryPush);

  const renderSubFilter = (config, labelId) => {
    if (!config) return null;
    const queryParamName = constructQueryParamName(config.key, config.scope);
    const { filterConfig = {} } = config;
    return (
      <SelectMultipleFilter
        id={`${prefix}.CombinedWheelFilter.${config.key}`}
        className={css.subFilter}
        label={intl.formatMessage({ id: labelId })}
        name={config.key}
        queryParamNames={[queryParamName]}
        initialValues={initialValues([queryParamName], liveEdit)}
        onSubmit={handleChange}
        options={config.enumOptions}
        schemaType={config.schemaType}
        searchMode={filterConfig.searchMode}
        liveEdit={liveEdit}
        showAsPopup={showAsPopup}
        {...subRest}
      />
    );
  };

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.filterHeader}>
        <button
          type="button"
          className={css.labelButton}
          onClick={() => setOpen(prev => !prev)}
          aria-expanded={open}
        >
          <span className={css.label}>
            {intl.formatMessage({ id: 'CombinedWheelFilter.label' })}
          </span>
          <span className={css.openSign}>
            <IconPlus isOpen={open} />
          </span>
        </button>
      </div>

      {open ? (
        <div className={css.body}>
          {renderSubFilter(diameterConfig, 'CombinedWheelFilter.diameter')}
          {renderSubFilter(widthFrontConfig, 'CombinedWheelFilter.rimWidthFront')}
          {renderSubFilter(widthRearConfig, 'CombinedWheelFilter.rimWidthRear')}
        </div>
      ) : null}
    </div>
  );
};

CombinedWheelFilter.defaultProps = {
  diameterConfig: null,
  widthFrontConfig: null,
  widthRearConfig: null,
};

CombinedWheelFilter.propTypes = {
  idPrefix: string,
  diameterConfig: object,
  widthFrontConfig: object,
  widthRearConfig: object,
  initialValues: func.isRequired,
  getHandleChangedValueFn: func.isRequired,
  intl: object.isRequired,
  liveEdit: bool,
  showAsPopup: bool,
};

export default CombinedWheelFilter;
